import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  Alert,
  Button,
  Card,
  Divider,
  Error,
  Form,
  Input,
  SelectPrefecture,
  SEO,
  showMessage,
  Title,
} from 'components/common'
import Layout from 'components/layouts/tow-column'
import {
  CountryCode,
  useGeneralSettingQuery,
  useGeneralSettingUpdateMutation,
} from 'lib/graphql/storeadmin'

export const Head = () => <SEO title="一般設定" />

const GeneralSettingPage = (): JSX.Element => {
  // General error useState
  const [showGeneralErrorAlert, setShowGeneralErrorAlert] = useState(false)
  const [generalErrorDescription, setGeneralErrorDescription] = useState([])

  const [form] = Form.useForm()

  const { data, error, refetch, loading } = useGeneralSettingQuery()
  const [updateGeneralSetting] = useGeneralSettingUpdateMutation()

  const FieldsValues = useMemo(() => {
    if (data) {
      const { details, location, baseTimeUnit } = data.generalSetting
      return { ...details, ...location, baseTimeUnit }
    }
  }, [data])

  useEffect(() => {
    refetch()
    form.setFieldsValue(FieldsValues)
  }, [FieldsValues, form, refetch])

  const onFinish = async (values) => {
    const {
      name,
      contactEmail,
      fromEmail,
      companyName,
      phone,
      zip,
      prefectureCode,
      city,
      address1,
      address2,
      baseTimeUnit,
    } = values
    const variablesInput = {
      details: {
        name,
        contactEmail,
        fromEmail,
      },
      location: {
        companyName,
        countryCode: CountryCode.Jpn,
        prefectureCode,
        phone,
        zip,
        city,
        address1,
        address2,
      },
      baseTimeUnit: parseInt(baseTimeUnit),
    }
    try {
      const { data } = await updateGeneralSetting({
        variables: { input: variablesInput },
      })
      const storeErrors = data?.generalSettingUpdate?.storeUserErrors || []
      if (storeErrors.length > 0) {
        showMessage(
          'error',
          '入力内容に誤りがあります。ご確認の上、再度入力してください。'
        )
        setShowGeneralErrorAlert(true)
        const errorMessages = []
        storeErrors.forEach((value) => {
          switch (value.code) {
            case 'BAD_DOMAIN':
              errorMessages.push(
                '入力メールに無効なドメイン名が含まれています。'
              )
              break
            case 'BLANK':
              errorMessages.push('入力値が空白です。')
              break
            case 'CONTAINS_HTML_TAGS':
              errorMessages.push('入力にはHTMLタグが含まれています。')
              break
            case 'CONTAINS_URL':
              errorMessages.push('入力にURLが含まれています。')
              break
            case 'STORE_DISABLED':
              errorMessages.push('組織が無効になっています。')
              break
            case 'INVALID':
              if (
                value.field.indexOf('contactEmail') !== -1 ||
                value.field.indexOf('fromEmail') !== -1
              ) {
                errorMessages.push('メールの値が無効です。')
              } else {
                errorMessages.push('入力値が不正です。')
              }
              break
            case 'NOT_FOUND':
              errorMessages.push('ストアは存在しません。')
              break
            case 'TAKEN':
              if (
                value.field.indexOf('contactEmail') !== -1 ||
                value.field.indexOf('fromEmail') !== -1
              ) {
                errorMessages.push('メールの価値はすでに取得されています。')
              } else {
                errorMessages.push('入力値はすでに取得されています。')
              }
              break
            case 'TOKEN_INVALID':
              errorMessages.push('無効なアクティベーショントークン。')
              break
            case 'TOO_SHORT' || 'TOO_LONG':
              if (value.field.indexOf('name') !== -1) {
                errorMessages.push('名前を確認してください。')
              } else if (
                value.field.indexOf('contactEmail') !== -1 ||
                value.field.indexOf('fromEmail') !== -1
              ) {
                errorMessages.push('メールの値が無効です。')
              } else if (value.field.indexOf('phone') !== -1) {
                errorMessages.push(
                  '電話番号が有効であることを確認してください。'
                )
              } else if (value.field.indexOf('zip') !== -1) {
                errorMessages.push(
                  '郵便番号が有効であることを確認してください。'
                )
              } else if (value.field.indexOf('companyName') !== -1) {
                errorMessages.push('会社の正式名称をご確認ください。')
              } else if (
                value.field.indexOf('address1') !== -1 ||
                value.field.indexOf('address2') !== -1
              ) {
                errorMessages.push('住所を確認してください。')
              } else {
                errorMessages.push(
                  '入力値が長すぎたり短すぎたりしないことを確認してください'
                )
              }
              break
            case 'UNDEFINED_STORE':
              errorMessages.push('正体不明の店。')
              break
            case undefined:
              errorMessages.push(...errorMessages)
              break
            default:
              errorMessages.push('エラーが発生しました。')
              break
          }
        })
        const filteredErrorMessage = errorMessages?.filter(
          (e, i) => errorMessages?.indexOf(e) === i
        )
        setGeneralErrorDescription(filteredErrorMessage)
      } else if (error) {
        showMessage('error', 'エラーが発生しました。')
      } else {
        setShowGeneralErrorAlert(false)
        showMessage('success', '保存しました。')
      }
    } catch (err) {
      setShowGeneralErrorAlert(true)
      showMessage('error', 'サーバーとの通信時にエラーが発生しました。')
    }
  }

  const onCloseAlert = () => {
    setShowGeneralErrorAlert(false)
  }

  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <Form component="form" form={form} onFinish={onFinish}>
        <Layout title="一般設定" loading={loading}>
          <Layout.Section size="small">
            <Title level={4}>テナントの詳細</Title>
            <div>
              このサービスとお客様があなたに連絡を取る場合、この情報を使用します。
            </div>
          </Layout.Section>

          <Layout.Section size="large">
            {showGeneralErrorAlert && (
              <Alert
                className={clsx('mb-5')}
                message="エラー"
                description={
                  <div>
                    {generalErrorDescription.length > 0 &&
                      generalErrorDescription?.map((value, index) => (
                        <>
                          <p key={index.toString()}>
                            {index + 1}: {value}
                          </p>
                        </>
                      ))}
                  </div>
                }
                type="error"
                showIcon
                closable
                onClose={onCloseAlert}
              />
            )}
            <Card shadow loading={loading}>
              <Form.Item
                label="施設名"
                name="name"
                rules={[
                  {
                    required: true,
                    message: '施設名は必須です。',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Form.Item
                  label="アカウントのメールアドレス"
                  name="contactEmail"
                  style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
                  rules={[
                    {
                      type: 'email',
                      message: '正しいメールアドレスを入力してください',
                    },
                    {
                      required: true,
                      message: 'メールアドレスは必須です。',
                    },
                  ]}
                  help="アカウントに連絡する必要がある場合、このメールアドレスを使用します。"
                  validateTrigger="onBlur"
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label="差出人メールアドレス"
                  name="fromEmail"
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 8px)',
                    margin: '0 0 0 16px',
                  }}
                  rules={[
                    {
                      type: 'email',
                      message: '正しいメールアドレスを入力してください',
                    },
                    {
                      required: true,
                      message: 'メールアドレスは必須です。',
                    },
                  ]}
                  help="お客様にメールを送信すると、このメールアドレスが表示されます。"
                  validateTrigger="onBlur"
                >
                  <Input />
                </Form.Item>
              </Form.Item>
            </Card>
          </Layout.Section>

          <Divider space="large" />

          <Layout.Section size="small">
            <Title level={4}>施設の所在地</Title>
            <div>この住所は請求書に表示されます。</div>
          </Layout.Section>

          <Layout.Section size="large">
            <Card shadow loading={loading}>
              <Form.Item
                label="会社の正式名称"
                name="companyName"
                rules={[
                  {
                    required: true,
                    message: '法人・団体名は必須です。',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="電話番号"
                help="ハイフン無し、半角数字のみで入力してください。"
                name="phone"
                rules={[
                  {
                    pattern: new RegExp(/^[0-9]*$/),
                    message: '半角数字のみで入力してください',
                  },
                  {
                    required: true,
                    message: '電話番号は必須です。',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Form.Item
                  label="郵便番号"
                  name="zip"
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 8px)',
                  }}
                  rules={[
                    {
                      pattern: new RegExp(/^[0-9]*$/),
                      message: '半角数字のみで入力してください',
                    },
                    {
                      required: true,
                      message: '郵便番号は必須です。',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>

                <SelectPrefecture
                  required
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 8px)',
                    margin: '0 0 0 16px',
                  }}
                />
              </Form.Item>

              <Form.Item
                label="市区町村"
                name="city"
                rules={[
                  {
                    required: true,
                    message: '市区町村は必須です。',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="住所"
                name="address1"
                rules={[
                  {
                    required: true,
                    message: '住所は必須です。',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="建物名、部屋番号など"
                name="address2"
                rules={[
                  {
                    required: true,
                    message: '建物名、部屋番号なは必須です。',
                  },
                ]}
              >
                <Input autoComplete="off" />
              </Form.Item>
            </Card>
          </Layout.Section>

          <Divider space="large" />

          <Layout.Section size="large">
            <div className={clsx('flex', 'justify-end')}>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </div>
          </Layout.Section>
        </Layout>
      </Form>
    </>
  )
}

export default GeneralSettingPage
